import { AlertTriangle } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import type { StockTransformation } from "@artisancode/api-types";

interface Props {
  transformation: StockTransformation | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (id: string, reason: string) => Promise<void>;
  isSubmitting?: boolean;
}

export function RepackingCancelDialog({
  transformation,
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
}: Props) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (!transformation) return null;

  const isCompleted = transformation.status === "completed";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError("Alasan pembatalan wajib diisi.");
      return;
    }

    try {
      setError(null);
      await onConfirm(transformation.id, reason.trim());
      setReason("");
      onOpenChange(false);
    } catch {
      // handled by parent toast
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          setReason("");
          setError(null);
        }
        onOpenChange(isOpen);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            <DialogTitle>Batalkan Dokumen Repacking</DialogTitle>
          </div>
          <DialogDescription>
            Anda akan membatalkan dokumen repacking{" "}
            <span className="font-semibold text-foreground">
              {transformation.code}
            </span>
            .
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          {isCompleted ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
              <p className="font-semibold">Perhatian Reversal Stok:</p>
              <p className="mt-1">
                Dokumen ini sudah berstatus <strong>Selesai</strong>. Pembatalan
                akan otomatis membuat pergerakan pembalik (reversal) pada kartu
                stok: mengembalikan saldo ke wadah awal (on-hand) dan mengurangi
                saldo di wadah repacking serta membatalkan susut proses.
              </p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Dokumen draft ini akan ditandai dibatalkan tanpa mempengaruhi
              saldo kartu stok.
            </p>
          )}

          <div className="space-y-2">
            <Label htmlFor="cancel-reason" className="text-xs font-medium">
              Alasan Pembatalan <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="cancel-reason"
              placeholder="Contoh: Salah timbangan wadah, toples rusak, dll..."
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (error) setError(null);
              }}
              rows={3}
              className="resize-none text-xs"
              autoFocus
            />
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>

          <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="min-h-[40px] sm:min-h-9"
            >
              Kembali
            </Button>
            <Button
              type="submit"
              variant="destructive"
              size="sm"
              disabled={isSubmitting || !reason.trim()}
              className="min-h-[40px] sm:min-h-9"
            >
              {isSubmitting ? "Membatalkan..." : "Ya, Batalkan Dokumen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
